import type { ExperienceListFilters } from 'actions/ExperienceActions'
import Clickable from 'components/Common/Clickable'
import Image from 'components/Common/Image'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import { useAppSelector } from 'hooks/reduxHooks'
import { filterCollinsonLounges, collinsonLoungeSupportsFlightType, getExperienceListKey } from 'lib/experiences/experienceUtils'
import { getIsInternationalFlight } from 'checkout/selectors/view/flights'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import LoyaltyTierBenefitAvailabilityMessageBanner from 'loyalty/components/LoyaltyTierBenefitAvailabilityMessageBanner'
import useLoyaltyConfig from 'loyalty/hooks/useLoyaltyConfig'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import Divider from 'LuxKit/Divider'
import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import LineMapMarkerIcon from 'LuxKit/Icons/line/LineMapMarkerIcon'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { rem } from 'polished'
import { Fragment, useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import styled from 'styled-components'
import focusStyle from 'styles/utils/focusStyle'
import { EmptyExperienceList } from 'constants/experience'
import { unique } from 'lib/array/arrayUtils'

const AirportTile = styled(Clickable)`
  display: grid;
  padding: ${rem(12)};
  margin-left: -${rem(12)};
  margin-right: -${rem(12)};
  grid-template-columns: ${rem(84)} 1fr;
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  transition: background-color 0.2s;
  gap: ${rem(12)};

  ${mediaQueryUp.tablet} {
    gap: ${rem(20)};
    grid-template-columns: ${rem(140)} 1fr;
  }


  ${mediaHoverable} {
    &:hover {
      background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
    }
  }

  &:focus-visible {
    ${focusStyle}
  }
`

const AirportTileContent = styled(Group)`
  padding: ${rem(12)} 0;

  ${mediaQueryUp.tablet} {
    padding: ${rem(28)} 0;
  }
`

interface MappedProps {
  experienceLists: App.ExperienceListState
}

interface Props extends MappedProps {
  onAirportSelect?: (experience: App.ExperienceOffer) => void
  locationsCountryCodes: Array<string>
  availabilityFilters: Array<ExperienceListFilters>
}

function LoyaltyLoungePassModalSelect(props: Props) {
  const { onAirportSelect, availabilityFilters, experienceLists, locationsCountryCodes } = props
  const intl = useIntl()

  const [, fetchingLoyaltyConfig] = useLoyaltyConfig()

  const modalHeaderTitle = intl.formatMessage({
    id: 'pcKhRfsQQLrYPAidlbas',
    defaultMessage: 'Free airport lounge passes',
    description: 'Modal header title for lounge pass selection dialog window',
  })
  const chooseLoungeHeading = intl.formatMessage({
    id: 'a9mklsSotFVRFlHM03cE',
    defaultMessage: 'Choose your preferred lounge',
    description: 'Heading prompting user to select their preferred airport lounge',
  })
  const multipleLocationsText = intl.formatMessage({
    id: '35leCBYjeucnHHyuySLB',
    defaultMessage: 'Multiple Locations',
    description: 'Label for lounge with multiple redemption locations',
  })
  const disclaimerText = intl.formatMessage({
    id: 'w97Hg62ZOLkfWE18JSKK',
    defaultMessage: 'Lounge access is available exclusively at the above airports.',
    description: 'Disclaimer text about airport lounge pass availability and validity per booking',
  })

  const experiences = useAppSelector((state) => state.experience.experiences)
  const spoofed = useAppSelector((state) => state.auth.account.isSpoofed)

  const isInternational = useAppSelector(getIsInternationalFlight)

  const loungeList = useMemo(() => {
    const filteredPerGroup = availabilityFilters.map(filters => {
      const listKey = getExperienceListKey(filters)
      const list = experienceLists[listKey] ?? EmptyExperienceList
      const lounges = list.ids.map(id => experiences[id]).filter(Boolean)
        .filter(exp => {
          const tickets = (exp as any).tickets as Array<{ isLoyaltyEligible?: boolean }> | undefined
          return !tickets?.length || tickets.some(t => t.isLoyaltyEligible)
        })
        .filter(exp => collinsonLoungeSupportsFlightType(exp, isInternational))
      return spoofed ? lounges : filterCollinsonLounges(lounges, l => l.provider)
    })

    const ids = unique(filteredPerGroup.flat().map(l => l.id))
    return ids.map(id => experiences[id]).filter(Boolean)
  }, [availabilityFilters, experiences, experienceLists, isInternational, spoofed])

  const bodyText = intl.formatMessage({
    id: '8YxwEn7zqZliGovSKUlF',
    defaultMessage: 'Enjoy peace of mind and comfort before your flight.',
    description: 'Description of airport lounge benefit for loyalty members',
  })

  const onAirportClick = useCallback<React.MouseEventHandler<HTMLButtonElement>>((e) => {
    const loungeId = e.currentTarget.dataset.id
    const lounge = loungeList.find(lounge => lounge.id === loungeId)
    if (lounge) {
      onAirportSelect?.(lounge)
    }
  }, [loungeList, onAirportSelect])

  // sort lounges, first AU, second what is in locationsCountryCodes, third the rest
  const sortedLounges = useMemo(() => {
    const auLounges = loungeList.filter(lounge => lounge.redemptionLocations.some(location => location.countryCode === 'AU'))
    const sameCountryAsOfferLounges = loungeList.filter(lounge => {
      return locationsCountryCodes.some(code => lounge.redemptionLocations.some(location => location.countryCode === code)) && !auLounges.includes(lounge)
    })
    const unsorted = loungeList.filter(lounge => !auLounges.includes(lounge) && !sameCountryAsOfferLounges.includes(lounge))
    return [...auLounges, ...sameCountryAsOfferLounges, ...unsorted]
  }, [loungeList, locationsCountryCodes])

  return (
    <>
      <ModalHeader title={modalHeaderTitle} />
      <ModalBody>
        <ModalContent>
          <VerticalSpacer gap={20}>
            <MessageBanner kind="warning" icon={<LineInfoCircleIcon />}>
              {intl.formatMessage({
                defaultMessage: 'Traveller names must match your boarding pass exactly for lounge access',
                id: '2ECSN7tKvZA2CsHTNk89',
                description: 'Warning banner in lounge selection modal about boarding pass name matching',
              })}
            </MessageBanner>
            <LoyaltyTierBenefitAvailabilityMessageBanner benefitType="lounge_pass" />
            <VerticalSpacer gap={24}>
              <VerticalSpacer gap={4}>
                <Heading variant="heading4">{chooseLoungeHeading}</Heading>
                <BodyText variant="medium">
                  {bodyText}
                </BodyText>
              </VerticalSpacer>
              <Group direction="vertical">
                {fetchingLoyaltyConfig && <VerticalSpacer gap={12}>
                  <LoadingBox height={84} />
                  <Divider kind="primary" />
                  <LoadingBox height={84} />
                  <Divider kind="primary" />
                  <LoadingBox height={84} />
                  <Divider kind="primary" />
                  <LoadingBox height={84} />
                  <Divider kind="primary" />
                </VerticalSpacer>}
                {!fetchingLoyaltyConfig && sortedLounges.length > 0 && sortedLounges.map((lounge) => <Fragment key={lounge.id}>
                  <AirportTile
                    onClick={onAirportClick}
                    data-id={lounge.id}
                  >
                    <Image image={lounge.images[0]} width={140} fit="center" />
                    <AirportTileContent
                      direction="horizontal"
                      verticalAlign="center"
                      horizontalAlign="space-between"
                      gap={12}
                    >
                      <Group direction="vertical" gap={4} desktopGap={12}>
                        <BodyTextBlock variant="small" weight="bold" startIcon={<LineMapMarkerIcon aria-hidden="true" />}>
                          {lounge.redemptionLocations?.length > 1 ? multipleLocationsText : lounge.location.name}
                        </BodyTextBlock>
                        <Heading variant="heading6">{lounge.name}</Heading>
                      </Group>
                      <LineAngleRightIcon />
                    </AirportTileContent>
                  </AirportTile>
                  <Divider kind="primary" />
                </Fragment>)}
                <BodyText variant="small" colour="light-foreground-alt">
                  {disclaimerText}
                </BodyText>
              </Group>
            </VerticalSpacer>
          </VerticalSpacer>

        </ModalContent>
      </ModalBody>
    </>
  )
}

function mapStateToProps(state: App.State) {
  return {
    experienceLists: state.experience.experienceLists,
  }
}

export default connect(mapStateToProps)(LoyaltyLoungePassModalSelect)
